import React, { useCallback, useEffect, useState, useRef } from 'react'
import Dashboard from '.';
import { Button, Skeleton, Dropdown, Modal as AntdModal, Tooltip, Select } from 'antd';
import { useLocation } from 'react-router-dom';
import {
  useLazyGetChatHistoryQuery,
  useLazyGetConversationHistoryQuery,
  useLazyGetBookmarksQuery,
  useCreateDashboardMutation,
  useUpdateChatNameMutation,
  useDeleteChatHistoryMutation,
  useLazyEditDashboardQuery,
  useBookMarkConversationMutation,
  useDeleteDashboardMutation
} from '../../services/dashboardSlice';
import { useGetGeneralSettingsQuery } from '../../services/settingsSlice';
import { useNavigate } from 'react-router-dom';
import useModal from '../../Hooks/useModal';
import { useForm, FormProvider } from 'react-hook-form';
import { ChevronOutlineRightIcon, EditChatIcon, PlusIcon, MenuIcon, TrashIcon, PinChatIcon, BookmarkIcon, DeleteRedIcon, SearchIcon, CloseIcon, UserIcon, IntegrationIcon, SettingsIcon, LogoutIcon, ChevronSelectorVertical, HelpIcon } from '../../components/Icons/iconImages';
import IconRings from '../../components/IconRings';
import InputField from '../../components/InputField';
import gsap from 'gsap';
import { Modal } from '../../components/Modal';
import { validationConstants } from '../../constants/validations';
import Conversation from '../Conversation';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from '../../components/Toast/toast';
import { useWorkspace } from '../../contexts/WorkspaceContext';
import { useSidebar } from '../../contexts/SidebarContext';
import { useOnboardingTour } from '../../Hooks/useOnboardingTour';
import { getTour } from '../../components/OnboardingTour/tourConfigs';
import OnboardingTour from '../../components/OnboardingTour/SafeOnboardingTour';
import TourDebugHelper from '../../components/OnboardingTour/TourDebugHelper';
import { updateNewConversation } from '../../services/staticSlice';
import { useGetCustomersEmailQuery } from '../../services/createProfile';
import { logOut } from '../../store/slice/userAuth';
import { getAuth, signOut as firebaseSignOut } from 'firebase/auth';
import { signOutCognitoUser } from '../../services/auth';
import { baseApi } from '../../services/baseQuery';

function DashboardLayout({ pathName }) {
  const contentRef = React.useRef(null);
  const [activeDashboard, setActiveDashboard] = useState(localStorage.getItem('activeDashboard') || '');
  const navigate = useNavigate();
  const location = useLocation();
  const { isMobileMenuOpen, isSidebarOpen } = useSidebar();
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  // First-time tour setup - kicks off the entire tour sequence
  const firstTimeTourData = getTour('first-time');

  console.log('[DASHBOARD] First-time tour data:', firstTimeTourData);

  const {
    runTour: runFirstTimeTour,
    currentTour: currentFirstTimeTour,
    closeTour: closeFirstTimeTour,
    skipTour: skipFirstTimeTour,
    startTour: startFirstTimeTour
  } = useOnboardingTour(firstTimeTourData, {
    delay: 2000, // Wait 2 seconds before showing first-time tour
    autoStart: true, // Auto-start for new users
    maxViewCount: 1, // Only show once
    chainToNext: true // Enable tour chaining - will automatically go through all tours
  });

  console.log('[DASHBOARD] Tour state:', { runFirstTimeTour, hasCurrentTour: !!currentFirstTimeTour });

  // Listen for manual first-time tour start
  useEffect(() => {
    const handleStartTour = (event) => {
      const tourData = event.detail;
      if (tourData && tourData.id === 'first-time-tour' && !runFirstTimeTour) {
        startFirstTimeTour(tourData);
      }
    };

    window.addEventListener('startTour', handleStartTour);
    return () => {
      window.removeEventListener('startTour', handleStartTour);
    };
  }, [startFirstTimeTour, runFirstTimeTour]);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <div className={"h-[calc(100vh-10.7rem)] relative"}>
      <SidebarComponent
        contentRef={contentRef}
        activeDashboard={activeDashboard}
        setActiveDashboard={setActiveDashboard}
        isMobile={isMobile}
      />
      {/* Development Tour Helper */}
      <TourDebugHelper />

      {/* First-time User Tour */}
      <OnboardingTour
        isOpen={runFirstTimeTour}
        tourData={currentFirstTimeTour}
        onComplete={closeFirstTimeTour}
        onSkip={skipFirstTimeTour}
      />

      <div
        ref={contentRef}
        className="transition-all duration-300 ease-in-out"
        style={{
          marginLeft: isMobile ? "0px" : (isSidebarOpen ? "226px" : "52px")
        }}
      >
        {pathName === "/conversation" ? <Conversation /> : <Dashboard activeDashboard={activeDashboard} />}
      </div>
    </div>
  )
}

const SidebarComponent = ({ contentRef, activeDashboard, setActiveDashboard, isMobile }) => {
  const location = useLocation();
  const { getCurrentWorkspaceObjectId, currentWorkspace, isInitialized, workspaces, switchWorkspace } = useWorkspace();
  const { isMobileMenuOpen, isSidebarOpen, toggleSidebar, closeMobileMenu } = useSidebar();
  const { data: userData } = useGetGeneralSettingsQuery('', {
    refetchOnMountOrArgChange: true,
  });
  const getUserData = userData?.settings;
  const dispatch = useDispatch();

  // Oogwai user check and impersonation
  const getEmail = localStorage.getItem("oogw.ai_user_email") || getUserData?.email;
  const oogwaiUser = getEmail && getEmail !== "info@oogw.ai" && getEmail !== "demo@oogw.ai" ? getEmail?.split('@')[1]?.includes('oogw.ai') : false;

  const { data: customersEmail } = useGetCustomersEmailQuery("", {
    skip: !oogwaiUser
  });

  const [shadowUser, setShadowUser] = useState(null);
  const storedShadowUser = localStorage.getItem('oogw.ai_shadow_user_email');

  useEffect(() => {
    setShadowUser(storedShadowUser && storedShadowUser !== 'undefined' ? storedShadowUser : null);
  }, [storedShadowUser]);

  // Onboarding tour for dashboard
  const dashboardTourData = getTour('dashboard');
  const {
    runTour,
    currentTour,
    closeTour,
    skipTour,
    resetTours,
    startTour
  } = useOnboardingTour(dashboardTourData, {
    delay: 1500, // Show tour after 1.5 seconds
    autoStart: false // Don't auto-start, only start manually from tour helper
  });

  // Listen for manual tour start events
  useEffect(() => {
    const handleStartTour = (event) => {
      const tourData = event.detail;
      // Only start this tour if it matches the dashboard tour and no tour is currently running
      if (tourData && tourData.id === 'dashboard-tour' && !runTour) {
        startTour(tourData);
      }
    };

    window.addEventListener('startTour', handleStartTour);
    return () => {
      window.removeEventListener('startTour', handleStartTour);
    };
  }, [startTour, runTour]);
  const navigate = useNavigate();
  const sidebarRef = React.useRef(null);
  const [formatSidebar, setFormatSideBar] = useState([]);
  const [deleteChatId, setDeleteChatId] = useState("");
  const [bookmarked, setBookmarked] = useState(false);
  // Remove local toggleSidebar state - now using context
  const [dashboardListData, setDashboardListData] = useState([]);
  const [isWorkspaceSwitching, setIsWorkspaceSwitching] = useState(false);
  // activeDashboard state is now managed by parent component

  const defaultValues = {
    new_chat_name: '',
    chat_id: "",
  };
  const {
    handleSubmit,
    control: newChatControl,
    formState: { errors },
    setValue: newChatSetValue
  } = useForm({
    defaultValues,
  });

  const {
    isOpen: isEditChatOpen,
    openModal: openEditChatModal,
    closeModal: closeEditChatModal,
    modalData: editChatModalData
  } = useModal();

  const {
    isOpen: isDeleteChatOpen,
    openModal: openDeleteChatModal,
    closeModal: closeDeleteChatModal,
    modalData: deleteChatModalData
  } = useModal();

  const [isSearching, setIsSearching] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const searchInputRef = React.useRef(null);

  const getWorkspaceId = useSelector((state) => state?.static?.workSpaceId) || getCurrentWorkspaceObjectId();

  const [getConversation, { data: getConversationData, isLoading: getConversationLoading }] = useLazyGetConversationHistoryQuery();
  const [getChatHistory, { data: chatData, isLoading: isChatHistoryLoading, isError: isChatHistoryError }] = useLazyGetChatHistoryQuery();
  const [getBookmarks, { isLoading: bookmarkLoading }] = useLazyGetBookmarksQuery();
  const [createDashboard] = useCreateDashboardMutation();
  const [updateChatName] = useUpdateChatNameMutation();
  const [deleteChatHistory] = useDeleteChatHistoryMutation();
  const [editDashboard] = useLazyEditDashboardQuery();
  const [deleteDashboard] = useDeleteDashboardMutation();

  const [chatId, setChatId] = useState("");
  const [data, setData] = useState([]);
  const [lastShadowUser, setLastShadowUser] = useState(localStorage.getItem('oogw.ai_shadow_user_email'));

  // Dashboard modal states
  const [isCreateDashboardOpen, setIsCreateDashboardOpen] = useState(false);
  const [isEditDashboardOpen, setIsEditDashboardOpen] = useState(false);
  const [isDeleteDashboardOpen, setIsDeleteDashboardOpen] = useState(false);
  const [editingDashboardName, setEditingDashboardName] = useState('');
  const [originalDashboardName, setOriginalDashboardName] = useState('');
  const [dashboardToDelete, setDashboardToDelete] = useState('');
  const [hoveredChatKey, setHoveredChatKey] = useState(null);

  // Conversation modal states - using deleteChatModalData from useModal instead

  // Form for creating dashboard
  const dashboardMethods = useForm({
    defaultValues: {
      dashboard_name: ''
    }
  });

  // Form for editing dashboard
  const editDashboardMethods = useForm({
    defaultValues: {
      dashboard_name: ''
    }
  });

  // Fetch dashboards
  const fetchDashboards = useCallback(async () => {
    
    try {
      let storedWorkspaceId = getCurrentWorkspaceObjectId();
      if (!storedWorkspaceId) {
        storedWorkspaceId = localStorage.getItem("workspace_id");
      }
      if (!storedWorkspaceId) {
        setDashboardListData([]);
        return Promise.resolve();
      }

      const response = await getBookmarks({ workspace_id: storedWorkspaceId }, { forceRefetch: true }).unwrap();
      if (!response.dashboards) {
        setDashboardListData([]);
        return Promise.resolve();
      }

      // Get the current active dashboard from localStorage
      const currentActiveDashboard = localStorage.getItem('activeDashboard');

      const dashboardList = response.dashboards.map(dashboard => ({
        name: dashboard.dashboard_name,
        isActive: dashboard.dashboard_name === currentActiveDashboard
      }));
      setDashboardListData(dashboardList);

      // Only set active dashboard if there's a valid one in localStorage
      if (currentActiveDashboard && dashboardList.some(d => d.name === currentActiveDashboard)) {
        // Ensure the active dashboard is set from localStorage only if it exists in the current workspace
        setActiveDashboard(currentActiveDashboard);
      } else {
        // Clear active dashboard if the stored one doesn't exist in current workspace
        setActiveDashboard('');
        localStorage.removeItem('activeDashboard');
      }

      return Promise.resolve();
    } catch (error) {
      console.error('Error fetching dashboards:', error);
      setDashboardListData([]);
      return Promise.resolve();
    }
  }, [getBookmarks, getCurrentWorkspaceObjectId]);

  // Track the last workspace to detect actual workspace changes (not just re-renders)
  const lastWorkspaceRef = useRef(null);
  const workspaceSwitchTimerRef = useRef(null);

  // Single effect to handle workspace initialization and changes
  useEffect(() => {
    // Only proceed if workspace context is initialized and we have a workspace
    if (isInitialized && currentWorkspace) {
      const currentWorkspaceId = getCurrentWorkspaceObjectId();

      // Check if this is an actual workspace change
      const isActualWorkspaceChange = lastWorkspaceRef.current !== null &&
        lastWorkspaceRef.current !== currentWorkspaceId;

      if (isActualWorkspaceChange) {
        // ONLY run this block on actual workspace switches
        setIsWorkspaceSwitching(true);

        // Reset newConversation flag during workspace switch to prevent clearing chatId
        dispatch(updateNewConversation(false));

        // Clear the active dashboard and dashboard list when workspace changes
        localStorage.removeItem('activeDashboard');
        setActiveDashboard('');
        setDashboardListData([]); // Clear existing dashboard list immediately

        // Clear any existing timer
        if (workspaceSwitchTimerRef.current) {
          clearTimeout(workspaceSwitchTimerRef.current);
        }

        // Small delay to prevent glitching, then fetch new data
        workspaceSwitchTimerRef.current = setTimeout(async () => {
          try {
            await getConversation();
            await fetchDashboards();

            // After fetching dashboards, auto-select the first one for workspace switch
            const storedWorkspaceId = getCurrentWorkspaceObjectId();
            if (storedWorkspaceId) {
              // Get fresh dashboard data to auto-select first dashboard
              try {
                const response = await getBookmarks({ workspace_id: storedWorkspaceId }, { forceRefetch: true }).unwrap();
                if (response.dashboards && response.dashboards.length > 0) {
                  const firstDashboard = response.dashboards[0].dashboard_name;
                  setActiveDashboard(firstDashboard);
                  localStorage.setItem('activeDashboard', firstDashboard);

                  // Update dashboard list to show first as active
                  const updatedDashboardList = response.dashboards.map(dashboard => ({
                    name: dashboard.dashboard_name,
                    isActive: dashboard.dashboard_name === firstDashboard
                  }));
                  setDashboardListData(updatedDashboardList);
                }
              } catch (error) {
                console.error('Error auto-selecting dashboard:', error);
              }
            }
          } catch (error) {
            console.error('Error in workspace switch:', error);
          } finally {
            setIsWorkspaceSwitching(false);
            workspaceSwitchTimerRef.current = null;
          }
        }, 100);

        // Update the last workspace ID immediately
        lastWorkspaceRef.current = currentWorkspaceId;
      } else if (lastWorkspaceRef.current === null) {
        // Initial load - just fetch data, don't auto-select
        setIsWorkspaceSwitching(true);

        const initTimer = setTimeout(async () => {
          try {
            await getConversation();
            await fetchDashboards();
          } finally {
            setIsWorkspaceSwitching(false);
          }
        }, 100);

        // Update the last workspace ID
        lastWorkspaceRef.current = currentWorkspaceId;

        return () => clearTimeout(initTimer);
      }
    }

    // Cleanup function - only clear timer on unmount
    return () => {
      if (workspaceSwitchTimerRef.current) {
        clearTimeout(workspaceSwitchTimerRef.current);
      }
    };
  }, [currentWorkspace, isInitialized, fetchDashboards, getConversation, getCurrentWorkspaceObjectId]);

  // Clear active dashboard when navigating to conversation route
  // But use a flag to prevent clearing when we're about to navigate away to dashboard
  const isNavigatingToDashboard = useRef(false);

  useEffect(() => {
    if (location.pathname === '/conversation' && !isNavigatingToDashboard.current) {
      setActiveDashboard('');
      localStorage.removeItem('activeDashboard');
    }
    // Reset the flag after navigation is complete
    if (location.pathname === '/dashboard') {
      isNavigatingToDashboard.current = false;
    }
  }, [location.pathname]);

  const handleToggleActiveDashboard = (name) => {
    // Set flag to prevent clearing during navigation
    isNavigatingToDashboard.current = true;

    localStorage.setItem('activeDashboard', name);
    setActiveDashboard(name);

    setDashboardListData((prevList) =>
      prevList.map((dashboard) => ({
        ...dashboard,
        isActive: dashboard.name === name,
      }))
    );

    // Close mobile menu when selecting a dashboard
    if (isMobile) {
      closeMobileMenu();
    }

    navigate('/dashboard');
  };

  const handleCreateDashboard = async () => {
    try {
      const dashboardName = dashboardMethods.getValues('dashboard_name');
      if (!dashboardName) {
        toast.error('Please enter a dashboard name');
        return;
      }

      const response = await createDashboard({ dashboard_name: dashboardName }).unwrap();

      if (response.message) {
        toast.success('Dashboard created successfully');
        dashboardMethods.reset();
        setIsCreateDashboardOpen(false);

        // Set the new dashboard as active (explicit user action)
        localStorage.setItem('activeDashboard', dashboardName);
        setActiveDashboard(dashboardName);

        // Refresh dashboards
        await fetchDashboards();
      }
    } catch (error) {
      console.error('Error creating dashboard:', error);
      toast.error(error.data?.error || 'Failed to create dashboard');
    }
  };

  const handleEditDashboard = async () => {
    try {
      if (originalDashboardName === editingDashboardName) {
        toast.info('Enter a different name');
        return;
      }

      const payload = {
        old_name: originalDashboardName,
        new_name: editingDashboardName,
      };

      const response = await editDashboard(payload).unwrap();

      if (response.message) {
        toast.success('Dashboard updated successfully');

        // Check if we're editing the currently active dashboard
        const wasActiveDashboard = localStorage.getItem('activeDashboard') === originalDashboardName;

        // Update localStorage if this was the active dashboard
        if (wasActiveDashboard) {
          localStorage.setItem('activeDashboard', editingDashboardName);
          setActiveDashboard(editingDashboardName);
        }

        // Directly update the dashboard list state without refetching
        setDashboardListData((prevList) =>
          prevList.map((dashboard) =>
            dashboard.name === originalDashboardName
              ? { ...dashboard, name: editingDashboardName, isActive: wasActiveDashboard }
              : dashboard
          )
        );

        // Close modal
        setIsEditDashboardOpen(false);

        // Invalidate cache for future fetches
        dispatch(baseApi.util.invalidateTags(['Bookmarks', 'Dashboards']));
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error(error.data?.error || 'Failed to update dashboard');
    }
  };

  const handleDeleteDashboard = async () => {
    try {
      const payload = {
        dashboard_name: dashboardToDelete,
        workspace_id: getCurrentWorkspaceObjectId(),
      };

      const response = await deleteDashboard(payload).unwrap();

      if (response.message) {
        toast.success('Dashboard deleted successfully');
        setIsDeleteDashboardOpen(false);

        // If deleted dashboard was active, set another as active
        if (localStorage.getItem("activeDashboard") === dashboardToDelete) {
          const remainingDashboards = dashboardListData.filter(d => d.name !== dashboardToDelete);
          if (remainingDashboards.length > 0) {
            localStorage.setItem("activeDashboard", remainingDashboards[0].name);
            setActiveDashboard(remainingDashboards[0].name);
          }
        }

        await fetchDashboards();
      }
    } catch (error) {
      console.error('Delete Error:', error);
      toast.error(error.data?.error || 'Failed to delete dashboard');
    }
  };

  const handleEditChat = (data) => {
    const reqmodel = { ...data };
    updateChatName(reqmodel)
      .unwrap()
      .then((response) => {
        if (response) {
          toast.success(response?.message || "Chat name updated successfully");
          closeEditChatModal();
        }
      })
      .catch((error) => {
        toast.error(error?.data?.error || error || 'Unexpected error');

      })
  }

  const handleDeleteChat = async () => {
    try {
      if (!deleteChatModalData?.id) {
        toast.error('No chat selected for deletion');
        return;
      }

      const response = await deleteChatHistory({
        chat_id: deleteChatModalData.id
      }).unwrap();

      if (response.message) {
        toast.success('Conversation deleted successfully');
        closeDeleteChatModal();

        // Refresh conversation list
        await getConversation();
      }
    } catch (error) {
      console.error('Error deleting chat:', error);
      toast.error(error.data?.error || 'Failed to delete conversation');
    }
  };

  const getmenuInfo = (info) => {
    let chat_id = info?.key;

    if (!chat_id) {
      console.error('No chat_id found in info:', info);
      return;
    }

    setChatId(chat_id);
    dispatch(updateNewConversation(false))

    // Store in sessionStorage as an additional safeguard
    sessionStorage.setItem('isLoadingSpecificChat', 'true');

    // Close mobile menu when selecting a chat
    if (isMobile) {
      closeMobileMenu();
    }

    // Navigate to conversation with the chat ID
    navigate(`/conversation?chatid=${chat_id}`);
  }

  // Effect to handle conversation data formatting
  useEffect(() => {
    if (getConversationData) {
      let format = getConversationData ? Object.entries(getConversationData)?.map((res, index) => {
        return {
          key: `${index}`,
          label: res[0]?.charAt(0)?.toUpperCase() + res[0]?.slice(1),
          children: res[1]?.map((child, childInx) => {
            return {
              key: `${child?.chat_id}`,
              label: child?.chat_name,
              has_bookmark: child?.has_bookmark,
            }
          })
        }
      }) : []
      setFormatSideBar(format)
    }
  }, [getConversationData]);

  // Separate effect for URL-based chatId management
  // Only depend on pathname and search, not the entire location object
  useEffect(() => {

    // Only process if we're on the conversation page
    if (location.pathname !== '/conversation') {
      return;
    }

    // Use location.search from React Router, not window.location
    const urlParams = new URLSearchParams(location.search);
    const chatIdFromUrl = urlParams.get('chatid');

    // Check if we're on conversation page with a chatId in the path
    const pathParts = location.pathname.split('/');
    const chatIdFromPath = pathParts.length > 2 && pathParts[1] === 'conversation' ? pathParts[2] : null;

    // Use chatId from URL params or path
    const currentChatId = chatIdFromUrl || chatIdFromPath;

    if (currentChatId) {
      setChatId(currentChatId);
    } else {
      // DON'T clear if we already have a chatId set - might be a timing issue
      if (chatId) {
      } else {
        setChatId('');
      }
    }
  }, [location.pathname, location.search, chatId])

  // Clear chatId when workspace or impersonation changes
  useEffect(() => {
    // Check for shadow user changes via storage event
    const handleStorageChange = (e) => {
      if (e.key === 'oogw.ai_shadow_user_email') {
        setChatId('');
        setLastShadowUser(e.newValue);
      }
    };

    window.addEventListener('storage', handleStorageChange);

    // Check current shadow user
    const currentShadowUser = localStorage.getItem('oogw.ai_shadow_user_email');
    if (currentShadowUser !== lastShadowUser) {
      setChatId('');
      setLastShadowUser(currentShadowUser);
    }

    // Also clear when workspace changes and we're on conversation page without chatId in URL
    if (location.pathname === '/conversation') {
      // Check if there's no chatId in the URL
      const urlParams = new URLSearchParams(window.location.search);
      const chatIdFromUrl = urlParams.get('chatid');
      const pathParts = location.pathname.split('/');
      const chatIdFromPath = pathParts.length > 2 && pathParts[1] === 'conversation' ? pathParts[2] : null;

      if (!chatIdFromUrl && !chatIdFromPath) {
        setChatId('');
      }
    }

    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [currentWorkspace, location.pathname, lastShadowUser])

  // Scroll to active chat when chatId changes
  useEffect(() => {
    if (chatId && sidebarRef.current) {
      // Small delay to ensure DOM is updated
      setTimeout(() => {
        const activeChatElement = document.getElementById(`chat-item-${chatId}`);
        if (activeChatElement && sidebarRef.current) {
          // Calculate the position to scroll to
          const sidebarTop = sidebarRef.current.scrollTop;
          const chatTop = activeChatElement.offsetTop;
          const sidebarHeight = sidebarRef.current.clientHeight;
          const chatHeight = activeChatElement.clientHeight;

          // Check if the element is not fully visible
          const isAboveViewport = chatTop < sidebarTop;
          const isBelowViewport = (chatTop + chatHeight) > (sidebarTop + sidebarHeight);

          if (isAboveViewport || isBelowViewport) {
            // Scroll the element into view with smooth animation
            activeChatElement.scrollIntoView({
              behavior: 'smooth',
              block: 'center' // Center the active chat in the viewport
            });
          }
        }
      }, 100); // 100ms delay to ensure DOM updates
    }
  }, [chatId, formatSidebar]) // Also depend on formatSidebar to scroll when chat list updates

  // Prevent sidebar scroll from propagating to main content
  useEffect(() => {
    const sidebarElement = sidebarRef.current;

    const handleWheel = (e) => {
      const { scrollTop, scrollHeight, clientHeight } = sidebarElement;
      const isAtTop = scrollTop === 0;
      const isAtBottom = scrollTop + clientHeight >= scrollHeight - 1; // -1 for rounding errors

      // Prevent scroll propagation when at boundaries and trying to scroll further
      if ((isAtTop && e.deltaY < 0) || (isAtBottom && e.deltaY > 0)) {
        e.preventDefault();
        e.stopPropagation();
      }
    };

    if (sidebarElement) {
      sidebarElement.addEventListener('wheel', handleWheel, { passive: false });
    }

    return () => {
      if (sidebarElement) {
        sidebarElement.removeEventListener('wheel', handleWheel);
      }
    };
  }, []);

  const handleToggleSidebar = useCallback(() => {
    if (!sidebarRef.current || !contentRef.current) return;
    toggleSidebar();
  }, [toggleSidebar]);

  const handleLogout = useCallback(() => {
    dispatch(logOut());
    const auth = getAuth();
    firebaseSignOut(auth);
    localStorage.removeItem("oogw.ai_user");
    localStorage.removeItem("oogw.ai_user_type");
    localStorage.removeItem("workspace_id");
    localStorage.removeItem('oogw.ai_shadow_user_email');
    navigate("/login");
    window.location.reload();
    dispatch(baseApi.util.resetApiState());
    signOutCognitoUser();
  }, [dispatch, navigate]);

  return (
    <>
      {/* Mobile overlay with dim background */}
      {isMobileMenuOpen && (
        <div
          className="fixed inset-0 bg-black/70 z-20 md:hidden transition-opacity duration-300"
          onClick={closeMobileMenu}
        />
      )}

      {/* Sidebar */}
      <div
        data-tour="sidebar"
        className={`conversation_sidebar z-30 fixed bg-white transition-all duration-300 ease-in-out
          ${isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full'}
          ${isMobileMenuOpen && isMobile ? 'shadow-2xl' : ''}
          border-r-2 md:border-r border-gray-300
          md:translate-x-0
          md:z-10 flex flex-col`}
        style={{
          width: isMobile ? '100%' : (isSidebarOpen ? '250px' : '52px'),
          height: 'calc(100vh)',
          left: '0',
          paddingBottom: isMobile ? 'calc(env(safe-area-inset-top) + 64px)' : '0'
        }}
      >
        {/* Sidebar Header */}
        <div className={`flex items-center pt-5 w-full ${isSidebarOpen ? 'justify-between px-3' : 'justify-center px-1'}`}>
          {!isMobile && (
            <>
              {isSidebarOpen && <div className="flex-1"></div>}
              <button onClick={handleToggleSidebar} className="border border-gray-300 bg-gray-100 p-2 rounded-md" type="button">
                <ChevronOutlineRightIcon className={`w-3 h-3 ${!isSidebarOpen ? 'rotate-0' : 'rotate-180'}`} />
              </button>
            </>
          )}
        </div>

        {/* Workspace Selector - Mobile Only */}
        {isMobile && (
          <div className="px-3 py-3">
            <p className="text-xs font-medium text-gray-500 mb-2">Workspace</p>
            <Select
              onChange={async (value) => {
                if (value === 'add-profile') {
                  navigate('/create-profile');
                  closeMobileMenu();
                } else if (value && value !== currentWorkspace) {
                  // Switch workspace logic from HeaderNav
                  if (location.pathname.startsWith('/conversation')) {
                    dispatch(updateNewConversation(true));
                    try { localStorage.removeItem('latest_chat_id'); } catch (e) { /* ignore */ }
                    navigate('/conversation', { replace: true });
                  }

                  await switchWorkspace(value);
                  closeMobileMenu();
                }
              }}
              value={currentWorkspace || ""}
              className="w-full"
              dropdownStyle={{ width: 'auto' }}
              popupMatchSelectWidth={false}
              optionRender={(item) => {
                return (
                  <div className="flex items-center gap-2 py-1 justify-center cursor-pointer">
                    {item.value === 'add-profile' ? item.label : item.label}
                  </div>
                )
              }}
              dropdownRender={(menu) => (
                <div className="text-center">
                  {menu}
                </div>
              )}
            >
              {workspaces?.map((workspace) => (
                <Select.Option key={workspace._id} value={workspace._id}>
                  {workspace.workspace_id}
                </Select.Option>
              ))}
              <Select.Option value="add-profile">
                <Button color="primary" variant="outlined" icon={<PlusIcon />}>
                  Create Workspace
                </Button>
              </Select.Option>
            </Select>
          </div>
        )}

        {/* Scrollable Container - Dashboards + Chats */}
        <div className={`flex-1 overflow-y-auto overflow-x-hidden`} ref={sidebarRef}>
          {/* Dashboard List Section */}
          <div className={`historyList flex flex-col justify-between mb-1 py-2 px-3 relative transition-opacity duration-300 ${!isMobile && !isSidebarOpen ? 'hidden' : ''}`} data-tour="dashboard-list">
            <p className={`text-md font-medium mb-1 text-[#A4A7AE] mt-3 transition-opacity duration-300`}>
              Dashboards
            </p>
            <Skeleton loading={bookmarkLoading || isWorkspaceSwitching} active paragraph={false} className="space-y-1">
              {dashboardListData.map((dashboard, index) => (
                <div
                  key={dashboard.name}
                  className={`mb-1 flex font-semibold items-center justify-between group px-3 py-2 rounded-3xl transition-all ${isWorkspaceSwitching
                    ? 'cursor-not-allowed opacity-50'
                    : `cursor-pointer ${dashboard.isActive && location.pathname !== '/conversation' ? 'bg-[#F5FAF7] text-[#4C8067]' : 'hover:bg-gray-100'}`
                    }`}
                  onClick={() => !isWorkspaceSwitching && handleToggleActiveDashboard(dashboard.name)}
                >
                  <span className="text-sm truncate flex-1 text-[#717680] font-semibold">{dashboard.name}</span>
                  <Dropdown
                    menu={{
                      items: [
                        {
                          label: 'Edit',
                          key: 'edit',
                          disabled: isWorkspaceSwitching,
                          onClick: (e) => {
                            e.domEvent.stopPropagation();
                            if (!isWorkspaceSwitching) {
                              setEditingDashboardName(dashboard.name);
                              setOriginalDashboardName(dashboard.name);
                              editDashboardMethods.setValue('dashboard_name', dashboard.name);
                              setIsEditDashboardOpen(true);
                            }
                          }
                        },
                        ...(dashboardListData.length > 1 ? [{
                          label: 'Delete',
                          key: 'delete',
                          danger: true,
                          disabled: isWorkspaceSwitching,
                          onClick: (e) => {
                            e.domEvent.stopPropagation();
                            if (!isWorkspaceSwitching) {
                              setDashboardToDelete(dashboard.name);
                              setIsDeleteDashboardOpen(true);
                            }
                          }
                        }] : [])
                      ]
                    }}
                    trigger={['click']}
                    disabled={isWorkspaceSwitching}
                  >
                    <Button
                      type="text"
                      size="small"
                      icon={<MenuIcon className="w-3 h-3" />}
                      className={`transition-opacity ${isWorkspaceSwitching ? 'opacity-0' : 'opacity-0 group-hover:opacity-100'}`}
                      onClick={(e) => e.stopPropagation()}
                      disabled={isWorkspaceSwitching}
                    />
                  </Dropdown>
                </div>
              ))}
            </Skeleton>
            <Button
              onClick={() => !isWorkspaceSwitching && setIsCreateDashboardOpen(true)}
              className="text-[#535862] w-full"
              icon={<PlusIcon className="w-5 h-5 p-1 rounded-full" />}
              iconPosition="start"
              data-tour="create-dashboard"
              disabled={isWorkspaceSwitching}
            >
              Add Dashboard
            </Button>
          </div>

          {/* Sticky Chats Header */}
          {formatSidebar?.some(item => item.children.length > 0) && (
            <div className={`sticky top-0 bg-white z-10 px-3 py-2 ${!isMobile && !isSidebarOpen ? 'hidden' : ''}`}>
              <div className="flex items-center justify-between mb-1 relative">
                <div className="relative flex-1">
                  {/* Chats Text */}
                  <span
                    className={`text-md font-medium text-[#A4A7AE] absolute left-0 top-1/2 -translate-y-1/2 transition-all duration-300 ease-in-out pointer-events-none
          ${isSearching
                        ? '-translate-x-full opacity-0'
                        : 'translate-x-0 opacity-100'}`}
                  >
                    Chats
                  </span>

                  {/* Search Input */}
                  <input
                    ref={searchInputRef}
                    type="text"
                    placeholder="Search chats..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className={`absolute left-0 top-1/2 -translate-y-1/2 w-full py-1 rounded-md text-sm transition-all duration-300 ease-in-out
          ${isSearching
                        ? 'translate-x-0 opacity-100'
                        : 'translate-x-full opacity-0 pointer-events-none'}`}
                  />
                </div>

                {/* Right side buttons */}
                <div className="flex items-center ml-2 z-10 transition-all duration-300 opacity-100 pointer-events-auto"
                >
                  {!isSearching ? (
                    <Button
                      type="text"
                      shape="circle"
                      onClick={() => {
                        setIsSearching(true);
                        setTimeout(() => searchInputRef.current?.focus(), 300);
                      }}
                      icon={<SearchIcon className="w-4 h-4 text-gray-500" />}
                    />
                  ) : (
                    <Button
                      type="text"
                      shape="circle"
                      onClick={() => {
                        setIsSearching(false);
                        setSearchQuery('');
                        searchInputRef.current.blur();
                      }}
                      icon={<CloseIcon className="w-3 h-3 text-gray-500" />}
                    />
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Conversation History Section */}
          <div data-tour="chat-history" className={`px-3 ${!isMobile && !isSidebarOpen ? 'hidden' : ''}`}>
            <Skeleton
              loading={getConversationLoading}
              className='w-full'
              active
              title={false}
              paragraph={{ rows: 8, width: ['100%', '90%', '95%', '85%', '100%', '92%', '88%', '96%'] }}
            >
              {formatSidebar?.some(res =>
                res.children.some(child =>
                  child.label.toLowerCase().includes(searchQuery.toLowerCase())
                )
              ) ? (
                formatSidebar.map((res, index) => {
                  // Only filter children if searchQuery is not empty
                  const filteredChildren = searchQuery
                    ? res.children.filter(child =>
                      child.label.toLowerCase().includes(searchQuery.toLowerCase())
                    )
                    : res.children;

                  // If no children to show, skip this parent
                  if (filteredChildren.length === 0) return null;

                  return (
                    <div className="historyList transition-opacity duration-300" key={index}>
                      <div className="space-y-0.5">
                        {filteredChildren.map((child, childIndex) => {
                          const isActive = child.key === chatId;
                          const isHovered = hoveredChatKey === child.key;

                          return (
                            <div
                              key={childIndex}
                              id={`chat-item-${child.key}`}
                              className={`flex items-center px-3 py-2 rounded-3xl cursor-pointer transition-all ${isActive ? 'bg-gray-100 font-medium' : 'hover:bg-gray-50'}`}
                              onClick={() => getmenuInfo(child)}
                              onMouseEnter={() => setHoveredChatKey(child.key)}
                              onMouseLeave={() => setHoveredChatKey(null)}
                            >
                              <Tooltip
                                title={child?.label || ''}
                                placement="right"
                                overlayStyle={{ maxWidth: '400px', whiteSpace: 'normal', wordWrap: 'break-word' }}
                              >
                                <div className="flex-1 overflow-hidden">
                                  <span className="block whitespace-nowrap overflow-hidden text-ellipsis max-w-auto transition-all duration-300 text-md font-semibold text-[#717680]">
                                    {child?.label
                                      ? child.label.charAt(0).toUpperCase() + child.label.slice(1)
                                      : ''}
                                  </span>
                                </div>
                              </Tooltip>

                              {isHovered && (
                                <Dropdown
                                  menu={{
                                    items: [
                                      {
                                        label: 'Edit',
                                        key: 'edit',
                                        icon: <EditChatIcon className="w-3 h-3" />,
                                        onClick: (e) => {
                                          e.domEvent.stopPropagation();
                                          openEditChatModal();
                                          newChatSetValue('new_chat_name', child?.label);
                                          newChatSetValue('chat_id', child?.key);
                                          setDeleteChatId(child?.key);
                                          setBookmarked(child?.has_bookmark);
                                        },
                                      },
                                      { type: 'divider' },
                                      {
                                        label: 'Delete',
                                        key: 'delete',
                                        danger: true,
                                        icon: <TrashIcon className="w-3 h-3" />,
                                        onClick: (e) => {
                                          e.domEvent.stopPropagation();
                                          openDeleteChatModal({ id: child?.key, name: child?.label });
                                        },
                                      },
                                    ],
                                  }}
                                  trigger={['click']}
                                >
                                  <Button
                                    shape="circle"
                                    type="text"
                                    size="small"
                                    className="ml-1"
                                    onClick={(e) => e.stopPropagation()}
                                    icon={<MenuIcon className="w-3 h-3" />}
                                  />
                                </Dropdown>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })
              ) : (
                <div className="text-center py-6 text-gray-400 text-sm">
                  No chats found
                </div>
              )}
            </Skeleton>
          </div>
        </div>

        {/* Profile Section at Bottom - Mobile Only */}
        {isMobile && getUserData && (
          <div className='px-3 pb-safe flex-shrink-0' style={{ paddingBottom: 'max(0.75rem, env(safe-area-inset-bottom))' }}>
            <div className={`border border-gray-300 rounded-2xl bg-white z-40 transition-transform duration-300 relative ${isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full'}`}>
              <div className="w-full flex items-center gap-3 p-4">
                <div className="relative">
                  {getUserData?.profile_picture ? (
                    <img
                      src={getUserData.profile_picture}
                      alt={getUserData?.first_name || 'User'}
                      className="w-10 h-10 rounded-full object-cover"
                    />
                  ) : (
                    <UserIcon className="w-10 h-10 p-2 text-gray-600 rounded-full border border-gray-300 bg-white" />
                  )}
                  <div className="absolute bottom-0 right-0 w-2.5 h-2.5 border-2 border-white rounded-full bg-green-500" />
                </div>
                <div className="flex-1 text-left min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">
                    {getUserData?.first_name || getUserData?.last_name
                      ? `${getUserData?.first_name || ""} ${getUserData?.last_name || ""}`.trim()
                      : getUserData?.email?.split('@')[0] || 'User'}
                  </p>
                  <p className="text-xs text-gray-500 truncate">
                    {getUserData?.email || ''}
                  </p>
                </div>
                <Dropdown
                  menu={{
                    items: [
                      {
                        label: 'Integrations',
                        key: 'integrations',
                        icon: <IntegrationIcon className="w-4 h-4 text-[#535862]" />,
                        onClick: () => {
                          navigate('/integrations');
                          closeMobileMenu();
                        },
                      },
                      {
                        label: 'Settings',
                        key: 'settings',
                        icon: <SettingsIcon className="w-4 h-4 text-[#535862]" />,
                        onClick: () => {
                          navigate('/company-settings/general');
                          closeMobileMenu();
                        },
                      },
                      {
                        label: 'Report an Issue',
                        key: 'support',
                        icon: <HelpIcon className="w-4 h-4 text-[#535862]" />,
                        onClick: () => {
                          navigate('/support/report-issue');
                          closeMobileMenu();
                        },
                      },
                      ...(oogwaiUser && customersEmail?.emails && customersEmail.emails.length > 0 ? [
                        {
                          type: 'divider',
                        },
                        {
                          label: (
                            <div className="" onClick={(e) => e.stopPropagation()}>
                              <p className="text-xs font-medium text-gray-500 mb-2">Impersonate account</p>
                              <Select
                                showSearch
                                placeholder="Search for a user..."
                                value={shadowUser || undefined}
                                onChange={async (email) => {
                                  setShadowUser(email);
                                  localStorage.setItem('oogw.ai_shadow_user_email', email);
                                  sessionStorage.removeItem('referral_code');
                                  await new Promise(resolve => setTimeout(resolve, 50));
                                  dispatch(baseApi.util.resetApiState());
                                  if (location.pathname.startsWith('/conversation')) {
                                    dispatch(updateNewConversation(true));
                                    try { localStorage.removeItem('latest_chat_id'); } catch (e) { /* ignore */ }
                                    navigate('/conversation', { replace: true });
                                  }
                                  closeMobileMenu();
                                }}
                                filterOption={(input, option) =>
                                  (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                                }
                                className="w-full"
                                bordered={false}
                                placement="topLeft"
                                dropdownMatchSelectWidth={true}
                                dropdownStyle={{ zIndex: 99999 }}
                                getPopupContainer={() => document.body}
                                options={customersEmail.emails.map((email) => ({
                                  value: email,
                                  label: email,
                                }))}
                              />
                            </div>
                          ),
                          key: 'impersonate',
                        }
                      ] : []),
                      {
                        type: 'divider',
                      },
                      {
                        label: 'Sign out',
                        key: 'signout',
                        icon: <LogoutIcon className="w-4 h-4 text-[#535862]" />,
                        className: 'bg-gray-50',
                        onClick: () => {
                          handleLogout();
                          closeMobileMenu();
                        },
                      }
                    ]
                  }}
                  trigger={['click']}
                  placement="topRight"
                  overlayClassName="user-menu-dropdown"
                  overlayStyle={{ zIndex: 9999 }}
                >
                  <Button type="text" shape="circle">
                    <ChevronSelectorVertical className="w-4 h-4 text-gray-400" />
                  </Button>
                </Dropdown>
              </div>
            </div>
          </div>
        )}

      </div>

      {/* Create Dashboard Modal */}
      <AntdModal
        footer={null}
        open={isCreateDashboardOpen}
        centered
        className="xl:w-[35%] lg:w-[50%] md:w-[70%] sm:w-[80%]"
        data-tour="create-dashboard-modal"
      >
        <div className="p-5">
          <h4 className="text-lg font-semibold">Create a new dashboard</h4>
          <FormProvider {...dashboardMethods}>
            <div className="flex flex-col gap-4 mt-5" onClick={(e) => e.stopPropagation()}>
              <div data-tour="dashboard-name-input">
                <InputField
                  label="Enter a name for your new dashboard"
                  name="dashboard_name"
                  control={dashboardMethods.control}
                  placeholder="Example Analysis"
                  onClick={(e) => e.stopPropagation()}
                />
              </div>
              <div className="flex justify-end space-x-2 mt-1">
                <Button
                  type="default"
                  onClick={(e) => {
                    e.stopPropagation();
                    setIsCreateDashboardOpen(false);
                  }}
                >
                  Cancel
                </Button>
                <Button
                  type="primary"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleCreateDashboard();
                  }}
                  data-tour="submit-dashboard"
                >
                  Add to Dashboard
                </Button>
              </div>
            </div>
          </FormProvider>
        </div>
      </AntdModal>

      {/* Edit Chat Name Modal */}
      {
        isEditChatOpen && (
          <Modal
            onClose={() => {
              closeEditChatModal();
            }}
            className="xl:max-w-[55%] lg:max-w-[65%] md:max-w-[70%]"
          >
            <div className="flex flex-row items-start gap-5">
              <IconRings numRings={5} ringBaseSize={48} ringSpacing={56} className=" -z-10">
                <div className="bg-[#DCFAE6] p-3 w-fit h-fit rounded-full">
                  <BookmarkIcon className="text-[#17B26A] w-5 h-5" />
                </div>
              </IconRings>
            </div>
            <div className="z-10 pt-2">
              <h4 className=" text-nowrap">Edit Title</h4>
              <p className="text-sm">Change the name of your chat to something you can remember</p>
            </div>
            <form onSubmit={handleSubmit(handleEditChat)}>
              <div className='mt-4'>
                <InputField
                  control={newChatControl}
                  label={''}
                  placeholder={'Edit Title'}
                  type='text'
                  name="new_chat_name"
                  rules={{
                    required: 'Custom Name is required',
                    ...validationConstants.spaceNotAllows
                  }}
                  error={errors.new_chat_name}
                />
              </div>

              <div className="flex items-center gap-3 pt-5 mt-5">
                <Button
                  variant="outlined"
                  danger
                  className='w-1/2'
                  onClick={(e) => {
                    e.stopPropagation();
                    openDeleteChatModal(editChatModalData);
                  }}
                  icon={<DeleteRedIcon />}
                >
                  Delete Chat
                </Button>
                <Button className='w-1/2' type="primary" htmlType='submit'>Confirm</Button>
              </div>
            </form>
          </Modal>
        )
      }
      {/* Delete Chat Modal */}
      {
        isDeleteChatOpen && (
          <Modal
            onClose={() => {
              closeDeleteChatModal();
            }}
            className="xl:max-w-[55%] lg:max-w-[65%] md:max-w-[70%]"
          >
            <div className="relative p-4 flex flex-row items-start gap-5">
              <IconRings numRings={5} ringBaseSize={48} ringSpacing={56} className="-z-10">
                <div className="bg-[#FEE4E2] p-3 w-fit h-fit rounded-full">
                  <TrashIcon className="text-[#D92D20] w-5 h-5" />
                </div>
              </IconRings>
              <div className="z-10">
                <h4 className="whitespace-nowrap">Delete Conversation</h4>
                <p className="text-sm">Are you sure you want to delete this Conversation "{deleteChatModalData?.name}"? {bookmarked && <><br /> This conversation is added to bookmarks</>} </p>
              </div>
            </div>
            <div className="flex gap-2 flex-row justify-end items-center">
              <Button
                variant="outlined"
                onClick={() => closeDeleteChatModal()}
              >
                Cancel
              </Button>
              <Button
                danger
                onClick={handleDeleteChat}
                icon={<DeleteRedIcon />}
              >
                Delete Chat
              </Button>
            </div>
          </Modal>
        )
      }

      {/* Edit Dashboard Modal */}
      <AntdModal
        footer={null}
        onClose={() => setIsEditDashboardOpen(false)}
        open={isEditDashboardOpen}
        centered
        className="xl:w-[35%] lg:w-[50%] md:w-[70%] sm:w-[80%]"
      >
        <FormProvider {...editDashboardMethods}>
          <div className="p-5">
            <h4 className="text-lg font-semibold">Rename your dashboard</h4>
            <div className="flex flex-col gap-4 mt-5">
              <InputField
                label="Dashboard Name"
                name="editDashboardName"
                value={editingDashboardName} // Shows the previous name
                onChange={(e) => setEditingDashboardName(e.target.value)}
                placeholder={`Enter new dashboard name (current name: ${editingDashboardName})`}
              />
              <div className="flex justify-end space-x-2">
                <Button
                  type="default"
                  onClick={() => {
                    setIsEditDashboardOpen(false)
                  }}
                >
                  Cancel
                </Button>
                <Button
                  type="primary"
                  onClick={handleEditDashboard}
                >
                  Save Changes
                </Button>
              </div>
            </div>
          </div>
        </FormProvider>
      </AntdModal>

      {/* Delete Dashboard Modal */}
      <AntdModal
        footer={null}
        centered
        open={isDeleteDashboardOpen}
        onCancel={() => setIsDeleteDashboardOpen(false)}
      >
        <div className="relative p-4 flex flex-row items-start gap-5">
          <IconRings numRings={5} ringBaseSize={48} ringSpacing={56} className="-z-10">
            <div className="bg-[#FEE4E2] p-3 w-fit h-fit rounded-full">
              <TrashIcon className="text-[#D92D20] w-5 h-5" />
            </div>
          </IconRings>
          <div className="z-10">
            <h4 className="whitespace-nowrap">Delete Dashboard</h4>
            <p className="text-sm">
              Are you sure you want to delete the dashboard "{dashboardToDelete}"? This action cannot be undone.
            </p>
          </div>
        </div>
        <div className="p-4 flex flex-row justify-end items-center">
          <div className="space-x-4">
            <Button
              onClick={() => setIsDeleteDashboardOpen(false)}
            >
              Cancel
            </Button>
            <Button
              type="primary"
              danger
              onClick={handleDeleteDashboard}
            >
              Delete
            </Button>
          </div>
        </div>
      </AntdModal>

      {/* Onboarding Tour */}
      <OnboardingTour
        isOpen={runTour}
        tourData={currentTour}
        onComplete={closeTour}
        onSkip={skipTour}
      />
    </>
  )
}

export default DashboardLayout